import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import CtaBanner from "@/components/feature/CtaBanner";
import SectionHeading from "@/components/base/SectionHeading";
import StatStrip from "@/components/feature/StatStrip";
import TeamSection from "@/pages/about/components/TeamSection";
import { aboutStory, milestones, aboutReasons } from "@/mocks/about";
import { stats, visionMission } from "@/mocks/home";
import { HERO_IMAGES } from "@/config/heroImages";

export default function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="About Us"
        title="Lahari Packers and Movers, Bangalore"
        subtitle="Relocating families and businesses across Bangalore since 2009 with our own fleet, trained crews and a promise to treat every move like our own."
        crumbs={[{ label: "Home", href: "/" }, { label: "About us" }]}
        image={HERO_IMAGES.about}
      />

      {/* Story */}
      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto grid max-w-screen-2xl grid-cols-1 items-start gap-10 px-4 md:px-6 lg:grid-cols-2 lg:gap-14">
          <div className="aspect-[4/3] w-full overflow-hidden rounded-lg border border-background-200">
            <img
              src="/uploads/about-refrigerator-sofa.jpeg"
              alt="Furniture, refrigerator and packed goods secured inside the moving container - Lahari Packers and Movers Bangalore"
              title="Lahari Packers and Movers Bangalore loading"
              className="h-full w-full object-cover object-center"
            />
          </div>
          <div>
            <SectionHeading align="left" eyebrow="Our Story" title="17 years of careful moving" />
            <div className="mt-5 space-y-4">
              {aboutStory.map((paragraph) => (
                <p key={paragraph} className="text-sm leading-relaxed text-foreground-600 md:text-base">
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/request-a-quote"
                data-cta="Get a Free Quote"
                data-cta-location="about-story"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-file-text-line text-lg" aria-hidden="true" /> Get a Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Our Team content */}
      <TeamSection />

      <StatStrip stats={stats} />

      {/* Vision / mission / values */}
      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <SectionHeading
            eyebrow="What Drives Us"
            title="Our vision, mission and core values"
            subtitle="Three simple ideas that shape how we plan every move and treat every customer."
          />
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {visionMission.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-100 p-6">
                {item.image ? (
                  <img
                    src={item.image}
                    alt={`${item.title} - Lahari Packers and Movers Bangalore`}
                    title={`${item.title} - Lahari Packers and Movers Bangalore`}
                    className="h-16 w-16 object-contain"
                  />
                ) : (
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className={`${item.icon} text-2xl`} aria-hidden="true" />
                  </span>
                )}
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="bg-background-100 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <SectionHeading eyebrow="Our Journey" title="Milestones since 2009" />
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {milestones.map((item) => (
              <div key={item.year} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <span className="inline-flex items-center rounded-full bg-accent-100 px-3 py-1 font-heading text-sm font-bold text-accent-900">
                  {item.year}
                </span>
                <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reasons */}
      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <SectionHeading
            eyebrow="Why Choose Lahari"
            title="Reasons customers keep recommending us"
          />
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {aboutReasons.map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                  <i className={`${item.icon} text-xl`} aria-hidden="true" />
                </span>
                <div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}